import { PlusOutlined, SearchOutlined, SyncOutlined } from '@ant-design/icons'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { Badge, Button, Flex, Input, Tooltip, Typography } from 'antd'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth'
import {
    MessageTemplate,
    MessageTemplatePurpose,
    messageTemplateQuery,
    MessageTemplateQuery,
} from '../campaign/message-template'
import { MessageTemplateCreateModal } from '../campaign/components/message-template-create-modal'
import { MessageTemplateEditModal } from '../campaign/components/message-template-edit-modal'
import { MessageTemplateList } from '../campaign/components/message-template-list'
import {
    defaultTemplateSort,
    MessageTemplatePurposeFilter,
    MessageTemplateSort,
    MessageTemplateStatusFilter,
    MessageTemplateVisibilityFilter,
    TemplatePurposeFilterValue,
    TemplateSortValue,
    TemplateStatusFilterValue,
} from '../campaign/components/message-template-sort'
import { useMessageTemplates } from '../campaign/message-template-hooks'
import { NetworkErrors } from '../components/network-errors'
import { PageTitle } from '../components/page-title'
import { PortaledBreadcrumb } from '../components/portaled-breadcrumb'
import { TimeAgo } from '../components/time-ago'
import { useTitle } from '../hooks/use-title'
import {
    INDEX_WHATSAPP_BUSINESS_ACCOUNT,
    PRIVATE_MESSAGE_TEMPLATE,
    STORE_MESSAGE_TEMPLATE,
    SYNC_WHATSAPP_BUSINESS_ACCOUNT,
} from '../user/permission'
import { PhoneNumberList } from '../whatsapp-business-account/components/phone-number-list'
import { usePhoneNumbers } from '../whatsapp-business-account/phone-number-hooks'
import { useSyncWhatsappBusinessAccount, useWhatsappBusinessAccount } from '../whatsapp-business-account-hooks'

type TemplateFilters = NonNullable<MessageTemplateQuery['filters']>

type TemplateFilterKey = 'status' | 'public' | 'purpose'

type TemplateFilterValue<Key extends TemplateFilterKey> = NonNullable<TemplateFilters[Key]>[number]

type TemplateVisibility = 'all' | 'public' | 'private'

type PublicFilter = TemplateFilterValue<'public'>

const VISIBILITY_BY_PUBLIC_FILTER: Record<PublicFilter, TemplateVisibility> = {
    true: 'public',
    false: 'private',
}

const PUBLIC_FILTER_BY_VISIBILITY: Record<TemplateVisibility, PublicFilter | null> = {
    all: null,
    public: 'true',
    private: 'false',
}

export const Route = createFileRoute('/_auth/_main/whatsapp-business-accounts/$accountId/')({
    beforeLoad: ({ context }) => {
        if (!context.auth.can(INDEX_WHATSAPP_BUSINESS_ACCOUNT)) {
            throw redirect({
                to: '/dashboard',
            })
        }
    },
    validateSearch: messageTemplateQuery,
    component: ShowWhatsappBusinessAccount,
})

function ShowWhatsappBusinessAccount() {
    const { t } = useTranslation()
    const { can } = useAuth()
    const { accountId } = Route.useParams()
    const search = Route.useSearch()
    const navigate = Route.useNavigate()
    const accountQuery = useWhatsappBusinessAccount(accountId)
    const phoneNumbersQuery = usePhoneNumbers(accountId)
    const syncAccountMutation = useSyncWhatsappBusinessAccount(accountId)
    const [isCreateOpen, setIsCreateOpen] = useState(false)
    const [createPurpose, setCreatePurpose] = useState<MessageTemplatePurpose>('start')
    const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null)
    const [templateSearch, setTemplateSearch] = useState('')

    useTitle(t('WhatsApp business account'))

    const templateSort = search.sort ?? defaultTemplateSort
    const templateStatus: TemplateStatusFilterValue = search.filters?.status?.[0] ?? 'all'
    const publicFilter = search.filters?.public?.[0]
    const templateVisibility = publicFilter ? VISIBILITY_BY_PUBLIC_FILTER[publicFilter] : 'all'
    const templatePurpose: TemplatePurposeFilterValue = search.filters?.purpose?.[0] ?? 'all'
    const isFiltered = templateStatus !== 'all' || templateVisibility !== 'all' || templateSearch.trim() !== ''

    // An account holds a modest number of templates, and grouping by purpose
    // only works over the whole set, so load them in one go instead of
    // paginating. Purpose and search narrow on the client, so the purpose tabs
    // can count every purpose regardless of which one is open.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { purpose: _purpose, title: _title, ...serverFilters } = search.filters ?? {}
    const templatesQuery = useMessageTemplates({
        query: {
            size: 200,
            sort: templateSort,
            filters: {
                ...serverFilters,
                whatsappBusinessAccountId: [accountId],
            },
        },
    })

    const searchedTemplates = useMemo(() => {
        const needle = templateSearch.trim().toLowerCase()
        const templates = templatesQuery.data?.data ?? []

        if (!needle) {
            return templates
        }

        return templates.filter(
            (template) => template.title.toLowerCase().includes(needle) || template.body.toLowerCase().includes(needle)
        )
    }, [templatesQuery.data, templateSearch])

    const purposeCounts = useMemo(() => {
        const counts: Record<TemplatePurposeFilterValue, number> = {
            all: searchedTemplates.length,
            start: 0,
            recontact: 0,
            reject: 0,
        }

        searchedTemplates.forEach((template) => counts[template.purpose]++)

        return counts
    }, [searchedTemplates])

    // The account has no sync timestamp of its own; the freshest signal we have
    // is the most recently synced phone number.
    const lastSyncedAt = useMemo(() => {
        if (phoneNumbersQuery.isPending || phoneNumbersQuery.isError) {
            return null
        }

        const dates = phoneNumbersQuery.data.data
            .map((phone) => phone.lastSyncedAt)
            .filter((date): date is Date => !!date)

        if (dates.length === 0) {
            return null
        }

        return dates.reduce((latest, date) => (date > latest ? date : latest))
    }, [phoneNumbersQuery.data, phoneNumbersQuery.isPending, phoneNumbersQuery.isError])

    if (accountQuery.isPending || accountQuery.isError) {
        return (
            <NetworkErrors
                query={accountQuery}
                result404={{
                    title: t('WhatsApp business account not found'),
                }}
            />
        )
    }

    const account = accountQuery.data
    const templates =
        templatePurpose === 'all'
            ? searchedTemplates
            : searchedTemplates.filter((template) => template.purpose === templatePurpose)
    // Search earns its space once the list is long enough to lose things in.
    const showSearch = (templatesQuery.data?.data.length ?? 0) > 10 || templateSearch !== ''

    const openCreate = (purpose: MessageTemplatePurpose) => {
        setCreatePurpose(purpose)
        setIsCreateOpen(true)
    }

    const updateFilter = <Key extends TemplateFilterKey>(key: Key, value: TemplateFilterValue<Key> | null) => {
        navigate({
            search: (previous) => {
                const filters: TemplateFilters = { ...previous.filters }

                delete filters[key]

                if (value !== null) {
                    filters[key] = [value] as TemplateFilters[Key]
                }

                return {
                    ...previous,
                    page: undefined,
                    filters: Object.keys(filters).length > 0 ? filters : undefined,
                }
            },
        })
    }

    return (
        <>
            <PortaledBreadcrumb
                items={[
                    {
                        title: <Link to="/whatsapp-business-accounts">{t('WhatsApp business accounts')}</Link>,
                    },
                    {
                        title: account.name,
                    },
                ]}
            />
            <Flex
                vertical
                gap={32}
                className="py-1"
            >
                <Flex
                    vertical
                    gap={16}
                >
                    <Flex
                        justify="space-between"
                        align="start"
                        gap="middle"
                        className="flex-col sm:flex-row"
                    >
                        <Flex
                            vertical
                            gap={6}
                            className="min-w-0"
                        >
                            <Typography.Title
                                level={2}
                                className="mb-0 truncate text-[24px] font-normal leading-tight"
                            >
                                {account.name}
                            </Typography.Title>
                            <Flex
                                align="center"
                                gap={8}
                                wrap="wrap"
                                className="text-xs text-ink-tertiary"
                            >
                                <Badge
                                    status="success"
                                    text={<span className="text-xs text-ink-secondary">{t('Connected')}</span>}
                                />
                                <span aria-hidden>·</span>
                                <span>{t('Business ID')}</span>
                                <Typography.Text
                                    className="!text-ink-tertiary text-xs tabular-nums"
                                    copyable={{ text: account.whatsappId }}
                                >
                                    {account.whatsappId}
                                </Typography.Text>
                            </Flex>
                        </Flex>

                        {can(SYNC_WHATSAPP_BUSINESS_ACCOUNT) && (
                            <Tooltip title={t('Sync numbers and templates')}>
                                <Button
                                    className="shrink-0"
                                    icon={<SyncOutlined spin={syncAccountMutation.isPending} />}
                                    disabled={syncAccountMutation.isPending}
                                    onClick={() => {
                                        syncAccountMutation.mutate()
                                    }}
                                >
                                    <SyncLabel
                                        syncing={syncAccountMutation.isPending}
                                        lastSyncedAt={lastSyncedAt}
                                    />
                                </Button>
                            </Tooltip>
                        )}
                    </Flex>

                    <PhoneNumbers query={phoneNumbersQuery} />
                </Flex>

                <section className="border-t border-divider pt-7">
                    <PageTitle
                        title={t('Templates')}
                        className="mb-5"
                        extra={
                            <Flex
                                gap="small"
                                wrap="wrap"
                            >
                                <MessageTemplateStatusFilter
                                    value={templateStatus}
                                    onChange={(status) => updateFilter('status', status === 'all' ? null : status)}
                                />
                                {can(PRIVATE_MESSAGE_TEMPLATE) ? (
                                    <MessageTemplateVisibilityFilter
                                        value={templateVisibility}
                                        onChange={(visibility) =>
                                            updateFilter('public', PUBLIC_FILTER_BY_VISIBILITY[visibility])
                                        }
                                    />
                                ) : null}
                                <MessageTemplateSort
                                    value={templateSort}
                                    onChange={(sort: TemplateSortValue) => {
                                        navigate({
                                            search: (previous) => ({
                                                ...previous,
                                                page: undefined,
                                                sort,
                                            }),
                                        })
                                    }}
                                />
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    disabled={!can(STORE_MESSAGE_TEMPLATE)}
                                    onClick={() => {
                                        openCreate(templatePurpose === 'all' ? 'start' : templatePurpose)
                                    }}
                                >
                                    {t('Add {{name}}', { name: t('Template') })}
                                </Button>
                            </Flex>
                        }
                    />

                    <Flex
                        justify="space-between"
                        align="center"
                        gap="small"
                        wrap="wrap"
                        className="mb-6"
                    >
                        <MessageTemplatePurposeFilter
                            value={templatePurpose}
                            counts={purposeCounts}
                            onChange={(purpose) => updateFilter('purpose', purpose === 'all' ? null : purpose)}
                        />
                        {showSearch ? (
                            <Input
                                allowClear
                                value={templateSearch}
                                onChange={(event) => setTemplateSearch(event.target.value)}
                                prefix={<SearchOutlined className="text-ink-tertiary" />}
                                placeholder={t('Search templates')}
                                className="w-full sm:w-56"
                            />
                        ) : null}
                    </Flex>

                    {templatesQuery.isError ? (
                        <NetworkErrors query={templatesQuery} />
                    ) : (
                        <MessageTemplateList
                            templates={templates}
                            loading={templatesQuery.isPending}
                            grouped={templatePurpose === 'all'}
                            showEmptyGroups={!isFiltered && templatesQuery.isSuccess}
                            onEdit={setEditingTemplate}
                            onAdd={can(STORE_MESSAGE_TEMPLATE) ? openCreate : undefined}
                        />
                    )}

                    <MessageTemplateCreateModal
                        // Remount per purpose so the form starts from it.
                        key={createPurpose}
                        open={isCreateOpen}
                        purpose={createPurpose}
                        whatsappBusinessAccountId={account.id}
                        onCancel={() => setIsCreateOpen(false)}
                    />

                    <MessageTemplateEditModal
                        template={editingTemplate}
                        onCancel={() => setEditingTemplate(null)}
                    />
                </section>
            </Flex>
        </>
    )
}

type SyncLabelProps = {
    syncing: boolean
    lastSyncedAt: Date | null
}

function SyncLabel({ syncing, lastSyncedAt }: SyncLabelProps) {
    const { t } = useTranslation()

    if (syncing) {
        return t('Syncing…')
    }

    if (!lastSyncedAt) {
        return t('Sync numbers and templates')
    }

    return (
        <span>
            {t('Synced')} <TimeAgo date={lastSyncedAt} />
        </span>
    )
}

type PhoneNumbersProps = {
    query: ReturnType<typeof usePhoneNumbers>
}

function PhoneNumbers({ query }: PhoneNumbersProps) {
    if (query.isPending) {
        return <div className="h-11 animate-pulse rounded-xl bg-surface-muted" />
    }

    if (query.isError) {
        return <NetworkErrors query={query} />
    }

    return <PhoneNumberList phoneNumbers={query.data.data} />
}
